import { Container, Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { WalletStore } from '../stores/WalletStore';

export interface CoinBalanceOptions {
  x: number;
  y: number;
  /** Default 1. Coin icon scales with it; text font-size is fixed by `fontSize`. */
  scale?: number;
  fontSize?: number;
  /** Show an animated "+N" popup next to the balance when addCoins() is called. */
  animatePopups?: boolean;
}

/**
 * CoinBalance — reusable "💰 1,234" display. Reads the current coin balance
 * from WalletStore on construction and whenever `refresh()` is called.
 * Used in the shop top bar, menu header, and post-game coin reward popup.
 */
export class CoinBalance extends Container {
  private coinIcon: Sprite;
  private amountText: Text;
  private animatePopups: boolean;

  constructor(opts: CoinBalanceOptions) {
    super();
    this.position.set(opts.x, opts.y);
    const s = opts.scale ?? 1;
    const fs = opts.fontSize ?? 28;
    this.animatePopups = opts.animatePopups ?? false;

    this.coinIcon = new Sprite(Texture.from('ui_coin'));
    this.coinIcon.anchor.set(0.5);
    this.coinIcon.scale.set(s);
    this.addChild(this.coinIcon);

    this.amountText = new Text({
      text: CoinBalance.format(WalletStore.getCoins()),
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: fs,
        fill: 0xffd23f,
        stroke: { color: 0x1a0d22, width: 3 },
      },
    });
    this.amountText.anchor.set(0, 0.5);
    // Place text to the right of the coin with a small gap.
    this.amountText.position.set(this.coinIcon.width / 2 + 10, 0);
    this.addChild(this.amountText);
  }

  /** Re-read wallet and update the displayed number (no animation). */
  refresh(): void {
    this.amountText.text = CoinBalance.format(WalletStore.getCoins());
  }

  /**
   * Animate a "+N" popup floating up and fading, then refresh the balance.
   * Only runs if `animatePopups: true` was passed at construction.
   */
  showReward(amount: number): void {
    if (amount <= 0) {
      this.refresh();
      return;
    }
    if (!this.animatePopups) {
      this.refresh();
      return;
    }
    const popup = new Text({
      text: `+${amount}`,
      style: {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: (this.amountText.style.fontSize as number) + 4,
        fill: 0x79e8a3,
        stroke: { color: 0x0a3820, width: 3 },
      },
    });
    popup.anchor.set(0.5);
    popup.position.set(this.amountText.x + this.amountText.width / 2, 0);
    popup.alpha = 0;
    this.addChild(popup);
    gsap.to(popup, { alpha: 1, duration: 0.18, ease: 'sine.out' });
    gsap.to(popup, {
      y: -44,
      duration: 1.1,
      ease: 'power2.out',
    });
    gsap.to(popup, {
      alpha: 0,
      delay: 0.6,
      duration: 0.5,
      onComplete: () => {
        this.removeChild(popup);
        popup.destroy();
      },
    });
    this.refresh();

    // Small bump on the coin icon for celebration.
    gsap.fromTo(
      this.coinIcon.scale,
      { x: this.coinIcon.scale.x, y: this.coinIcon.scale.y },
      {
        x: this.coinIcon.scale.x * 1.3,
        y: this.coinIcon.scale.y * 1.3,
        duration: 0.18,
        yoyo: true,
        repeat: 1,
        ease: 'sine.out',
      },
    );
  }

  static format(coins: number): string {
    // Simple thousands separator (comma) — matches English conventions.
    return coins.toLocaleString('en-US');
  }
}
